<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soPriorityName"
        data-testid="so-priorities-name-input"
        v-model="formData.so_priority_name"
        :label="t('Nome da Prioridade')"
        :placeholder="t('Digite o nome da prioridade')"
        type="text"
        required
        :validation="validateName"
        fluid
      />
      <small v-if="errors.so_priority_name" class="error-message">{{ errors.so_priority_name }}</small>
    </div>

    <div class="form-field">
      <color-picker
        elem-id="soPriorityColor"
        v-model="formData.so_priority_color"
        :label="t('Cor de exibição')"
      />
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface PriorityRow {
  so_priority_id?: Id;
  so_priority_name?: string;
  so_priority_color?: string;
}

interface Props {
  itemId?: Id | null;
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { getById, save } = useSOCatalog('priorities');

// Convert itemId to number for consistency
const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  const num = typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
  log('SOPrioritiesForm', 'itemIdNumber', 'Converted ID', { original: props.itemId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{ so_priority_name: string; so_priority_color: string }>({
  so_priority_name: '',
  so_priority_color: ''
});

const errors = reactive<{ so_priority_name: string }>({
  so_priority_name: ''
});

const isLoading = ref<boolean>(false);

// Validation function for InputText component
const validateName = (): boolean => {
  return !!formData.so_priority_name && formData.so_priority_name.trim() !== '' && formData.so_priority_name.length <= 100;
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) {
    logWarn('SOPrioritiesForm', 'loadForEdit', 'No itemId provided');
    return;
  }

  log('SOPrioritiesForm', 'loadForEdit', 'Loading priority for edit', itemIdNumber.value);
  isLoading.value = true;

  try {
    const item = await getById(itemIdNumber.value) as PriorityRow | null;
    log('SOPrioritiesForm', 'loadForEdit', 'Loaded data', item);

    if (!item) {
      throw new Error('Dados da prioridade não encontrados');
    }

    // Populate form
    formData.so_priority_name = item.so_priority_name || '';
    formData.so_priority_color = item.so_priority_color || '';

    // Clear errors
    clearErrors();
  } catch (error) {
    const err = error as { message?: string; stack?: string; response?: { data?: { error?: string } } };
    logError('SOPrioritiesForm', 'loadForEdit', 'Error loading priority', {
      itemId: itemIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar prioridade') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOPrioritiesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_priority_name || formData.so_priority_name.trim() === '') {
    errors.so_priority_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_priority_name.length > 100) {
    errors.so_priority_name = t('Nome muito longo') + ' (max 100)';
    isValid = false;
  }

  log('SOPrioritiesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_priority_name = '';
};

const resetForm = (): void => {
  log('SOPrioritiesForm', 'resetForm', 'Resetting form');
  formData.so_priority_name = '';
  formData.so_priority_color = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('SOPrioritiesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOPrioritiesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload: PriorityRow = {
      so_priority_name: formData.so_priority_name.trim(),
      so_priority_color: formData.so_priority_color || ''
    };

    if (props.isEditMode && itemIdNumber.value) {
      // Update existing
      payload.so_priority_id = itemIdNumber.value;
      log('SOPrioritiesForm', 'handleSubmit', 'Updating priority', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Prioridade atualizada com sucesso'));
    } else {
      // Create new
      log('SOPrioritiesForm', 'handleSubmit', 'Creating priority', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Prioridade criada com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (error) {
    logError('SOPrioritiesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar prioridade') + ': ' + message
        : t('Erro ao criar prioridade') + ': ' + message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in itemId to reload data
watch(() => props.itemId, async (newId) => {
  log('SOPrioritiesForm', 'watch', 'itemId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('SOPrioritiesForm', 'mounted', 'Component mounted', {
    itemId: props.itemId,
    itemIdNumber: itemIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && itemIdNumber.value) {
    loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
